import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Feedback } from '../../models/feedback.model';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EnvService, GemValidators, TokenHelperService, ValueLabel } from 'gematik-form-library';
import { FeedbackFrontEndService } from '../../services/feedback-front-end.service';

@Component({
  selector: 'gem-feedback-inquiry-form',
  styleUrls: ['gem-feedback-inquiry-form.component.scss'],
  template: `
    <form [formGroup]="form">
      <div class="row mb-3">
        <div class="col-sm-12 mt-3">
          <p>{{ 'feedback.inquiryDepartment' | translate }}</p>
          <gem-select
            [placeholder]="'feedback.inquiryDepartmentPlaceholder' | translate"
            [control]="department"
            [options]="departmentOptions"
          ></gem-select>
        </div>

        <div class="col-sm-12 mt-3">
          <p>{{ 'feedback.inquiryTitleLabel' | translate }}</p>
          <gem-input [placeholder]="'feedback.inquiryTitlePlaceholder' | translate" [control]="title"></gem-input>
        </div>

        <div class="col-sm-12 mt-3">
          <p>{{ 'feedback.inquiryComments' | translate }}</p>
          <gem-textarea
            [placeholder]="'feedback.inquiryCommentsPlaceholder' | translate"
            [control]="comments"
            [maxChars]="500"
            [hint]="'(' + comments.value.length + ' / 500' + ')'"
          ></gem-textarea>
        </div>

        <div class="col-sm-12 mt-3">
          <p>{{ 'feedback.inquiryDocuments' | translate }}</p>
          <gem-file-drop-zone
            data-testid="documents"
            [outlined]="false"
            [label]="'gem-components.gem-file-upload.label' | translate"
            [allowedExtensions]="['.pdf', '.jpeg', '.jpg', '.png']"
            [multiple]="true"
            [files]="documents"
            (upload)="onFileUpload($event)"
            (remove)="onRemoveFile($event)"
          ></gem-file-drop-zone>
        </div>

        <div class="col-sm-12 mt-3">
          <p>{{ 'feedback.inquiryEmail' | translate }}</p>
          <gem-input [placeholder]="'feedback.inquiryEmailPlaceholder' | translate" [control]="email"></gem-input>
        </div>

        <div *ngIf="!isLoggedIn" class="col-sm-12 mt-3">
          <div class="checkbox-container disable-checkbox">
            <mat-checkbox [formControl]="dataPrivacy" [color]="'primary'">
              <span style="cursor: default" [innerHTML]="dataPrivacyLabel"></span>
            </mat-checkbox>
          </div>
        </div>

        <div class="col-sm-12 text-end mt-3">
          <button
            class="mat-focus-indicator btn btn-primary mat-button mat-button-base"
            style="display: inline-flex"
            cdkFocusInitial
            type="button"
            [disabled]="!isFormValid() || isLoading"
            (click)="onSubmit($event)"
          >
            {{ 'feedback.send' | translate }}
            <mat-spinner *ngIf="isLoading; else sendIcon" diameter="18" class="ms-2"></mat-spinner>
            <ng-template #sendIcon><mat-icon iconPositionEnd class="ms-2">send</mat-icon></ng-template>
          </button>
        </div>
      </div>
    </form>
  `,
  standalone: false,
})
export class GemFeedbackInquiryFormComponent implements OnInit {
  @Input() isLoading: boolean = false;
  @Output() back: EventEmitter<any> = new EventEmitter();
  @Output() feedback: EventEmitter<{ feedback: Feedback; documents: any[] }> = new EventEmitter();

  form = this.fb.group({
    category: ['INQUIRY', Validators.required],
    rating: [1],
    department: ['', Validators.required],
    title: ['', Validators.required],
    comments: ['', Validators.required],
    contact: ['WITH_EMAIL'],
    email: ['', [Validators.required, GemValidators.email]],
    documents: this.fb.array([]),
    dataPrivacy: [false, Validators.requiredTrue],
  });

  departmentOptions: ValueLabel[] = [];

  constructor(
    private fb: FormBuilder,
    public translateService: TranslateService,
    private tokenHelperService: TokenHelperService,
    private env: EnvService,
    private feedbackFrontEndService: FeedbackFrontEndService,
  ) {}

  ngOnInit(): void {
    this.departmentOptions = this.feedbackFrontEndService.getDepartmentOptions();

    if (this.tokenHelperService.isLoggedIn()) {
      this.email?.setValue(this.tokenHelperService.getEmail());
      this.dataPrivacy.setValue(true);
    } else {
      this.dataPrivacy.setValue(false);
    }
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  onFileUpload(event: FileList) {
    if (event) {
      Array.from(event).forEach((file: File) => {
        const index = this.documents.controls.findIndex(document => document.get('fileName')?.value === file.name);
        if (index === -1) {
          this.documents.push(
            this.fb.group({
              id: [null],
              fileName: [file.name],
              file: [file],
            }),
          );
        }
      });
    }
  }

  onRemoveFile(index: number): void {
    this.documents.removeAt(index);
  }

  onBack(): void {
    this.back.emit();
  }

  onSubmit(event: any): void {
    const feedback: Feedback = this.form.getRawValue() as Feedback;
    this.feedback.emit({ feedback: feedback, documents: this.documents.value });
    event.target.disabled = true;
  }

  get documents(): FormArray {
    return this.form.get('documents') as FormArray;
  }

  get dataPrivacy(): FormControl {
    return this.form.get('dataPrivacy') as FormControl;
  }

  get isLoggedIn(): boolean {
    return this.tokenHelperService.isLoggedIn();
  }

  get dataPrivacyLabel(): string {
    return this.translateService.instant('feedback.dataPrivacy', {
      link: `${this.env.uwlUrl}/data-privacy`,
    });
  }

  get email(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get department(): FormControl {
    return this.form.get('department') as FormControl;
  }

  get title(): FormControl {
    return this.form.get('title') as FormControl;
  }

  get comments(): FormControl {
    return this.form.get('comments') as FormControl;
  }
}
